import { getAuthToken, setAuthToken, clearAuthToken } from '../utils/authStorage';

import API_BASE_URL from '../config/apiBaseUrl';
import { APP_PREFIX } from '../config/appIdentifiers';
import { DEFAULT_FOOD_DETAIL_MACROS } from '../config/nutritionMacros';
import { LEGACY_FOOD_MACRO_KEY_MAP } from '../config/openFoodFactsNutrients';

const OFFLINE_MEAL_QUEUE_KEY = `${APP_PREFIX}:offline-meals:v1`;
const OFFLINE_MEAL_MUTATION_QUEUE_KEY = `${APP_PREFIX}:offline-meal-mutations:v1`;
const OFFLINE_MEAL_PREFIX = 'offline-meal-';

class JournalService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = getAuthToken();
  }

  getHeaders(contentType = 'application/json') {
    const headers = {
      'Content-Type': contentType,
      'Accept': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    return headers;
  }

  getStorage() {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.localStorage;
    } catch (error) {
      console.warn('LocalStorage non disponible:', error);
      return null;
    }
  }

  isOfflineMealId(mealId) {
    return typeof mealId === 'string' && mealId.startsWith(OFFLINE_MEAL_PREFIX);
  }

  isNetworkError(error) {
    if (!error) {
      return false;
    }

    if (error instanceof TypeError) {
      return true;
    }

    const message = String(error.message || '').toLowerCase();
    return message.includes('failed to fetch')
      || message.includes('networkerror')
      || message.includes('network error')
      || message.includes('load failed');
  }

  isNotFoundError(error) {
    const message = String(error?.message || '').toLowerCase();
    return message.includes('404') || message.includes('not found');
  }

  normalizeMacros(value, fallback = []) {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // ignore JSON parse errors
      }
    }
    return fallback;
  }

  mapLegacyMacroKeys(macros = []) {
    const mapped = macros.map((macro) => LEGACY_FOOD_MACRO_KEY_MAP[macro] || macro);
    return Array.from(new Set(mapped));
  }

  resolveFoodMacronutrients(foodData = {}) {
    const macronutrients = foodData.macronutrients;

    if (
      macronutrients
      && typeof macronutrients === 'object'
      && !Array.isArray(macronutrients)
    ) {
      return macronutrients;
    }

    return {};
  }

  normalizeFoodItem(food = {}) {
    if (!food || typeof food !== 'object') {
      return food;
    }

    return {
      ...food,
      macronutrients: this.resolveFoodMacronutrients(food),
    };
  }

  normalizeMeal(meal = {}) {
    if (!meal || typeof meal !== 'object') {
      return meal;
    }

    return {
      ...meal,
      foods: (meal.foods || []).map((food) => this.normalizeFoodItem(food)),
    };
  }

  getOfflineMealQueue() {
    const storage = this.getStorage();
    if (!storage) {
      return [];
    }

    try {
      const rawQueue = storage.getItem(OFFLINE_MEAL_QUEUE_KEY);
      if (!rawQueue) {
        return [];
      }
      const parsed = JSON.parse(rawQueue);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Impossible de lire la file hors ligne des repas:', error);
      return [];
    }
  }

  setOfflineMealQueue(queue) {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    try {
      storage.setItem(OFFLINE_MEAL_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.warn('Impossible de sauvegarder la file hors ligne des repas:', error);
    }
  }

  getOfflineMealMutationQueue() {
    const storage = this.getStorage();
    if (!storage) {
      return [];
    }

    try {
      const rawQueue = storage.getItem(OFFLINE_MEAL_MUTATION_QUEUE_KEY);
      if (!rawQueue) {
        return [];
      }
      const parsed = JSON.parse(rawQueue);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Impossible de lire la file de mutations hors ligne:', error);
      return [];
    }
  }

  setOfflineMealMutationQueue(queue) {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    try {
      storage.setItem(OFFLINE_MEAL_MUTATION_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.warn('Impossible de sauvegarder la file de mutations hors ligne:', error);
    }
  }

  queueMealMutation(journalId, type, payload = {}) {
    const normalizedJournalId = journalId ? String(journalId) : 'all';
    const queue = this.getOfflineMealMutationQueue();
    const clientId = `${Date.now()}-${Math.floor(Math.random() * 1e6).toString(16)}`;
    let optimizedQueue = [...queue];

    if (type === 'patchMeal' && payload.mealId) {
      const existingIndex = optimizedQueue.findIndex(
        (entry) => entry.type === 'patchMeal' && String(entry.payload?.mealId) === String(payload.mealId)
      );

      if (existingIndex >= 0) {
        const existing = optimizedQueue[existingIndex];
        optimizedQueue[existingIndex] = {
          ...existing,
          payload: {
            ...existing.payload,
            patch: {
              ...(existing.payload?.patch || {}),
              ...(payload.patch || {}),
            },
          },
          queuedAt: new Date().toISOString(),
        };
        this.setOfflineMealMutationQueue(optimizedQueue);
        return;
      }
    }

    if (type === 'deleteMeal' && payload.mealId) {
      optimizedQueue = optimizedQueue.filter((entry) => {
        if (entry.type === 'patchMeal' && String(entry.payload?.mealId) === String(payload.mealId)) {
          return false;
        }
        if (entry.type === 'deleteMeal' && String(entry.payload?.mealId) === String(payload.mealId)) {
          return false;
        }
        return true;
      });
    }

    if (type === 'reorderMeals') {
      optimizedQueue = optimizedQueue.filter(
        (entry) => !(entry.type === 'reorderMeals' && String(entry.journalId) === normalizedJournalId)
      );
    }

    optimizedQueue.push({
      clientId,
      journalId: normalizedJournalId,
      type,
      payload,
      queuedAt: new Date().toISOString(),
    });

    this.setOfflineMealMutationQueue(optimizedQueue);
  }

  createOfflineQueueEntry(journalId, payload) {
    const clientId = `${Date.now()}-${Math.floor(Math.random() * 1e6).toString(16)}`;
    return {
      clientId,
      journalId: String(journalId),
      payload,
      queuedAt: new Date().toISOString(),
    };
  }

  buildOfflineMealFromEntry(entry) {
    return {
      id: `${OFFLINE_MEAL_PREFIX}${entry.clientId}`,
      name: entry.payload?.name || 'Nouveau repas',
      mealTime: entry.payload?.mealTime || null,
      foods: [],
      sortOrder: Number.MAX_SAFE_INTEGER,
      isPendingSync: true,
      pendingSyncAt: entry.queuedAt,
    };
  }

  queueMealForOffline(journalId, payload) {
    const entry = this.createOfflineQueueEntry(journalId, payload);
    const queue = this.getOfflineMealQueue();
    queue.push(entry);
    this.setOfflineMealQueue(queue);
    return this.buildOfflineMealFromEntry(entry);
  }

  getPendingOfflineMeals(journalId) {
    if (!journalId) {
      return [];
    }

    return this.getOfflineMealQueue()
      .filter((entry) => String(entry.journalId) === String(journalId))
      .map((entry) => this.buildOfflineMealFromEntry(entry));
  }

  renamePendingMeal(mealId, newName) {
    if (!this.isOfflineMealId(mealId)) {
      return false;
    }

    const clientId = mealId.replace(OFFLINE_MEAL_PREFIX, '');
    const queue = this.getOfflineMealQueue();
    let updated = false;

    const updatedQueue = queue.map((entry) => {
      if (entry.clientId !== clientId) {
        return entry;
      }

      updated = true;
      return {
        ...entry,
        payload: {
          ...entry.payload,
          name: newName,
        },
      };
    });

    if (updated) {
      this.setOfflineMealQueue(updatedQueue);
    }

    return updated;
  }

  updatePendingMeal(mealId, patch = {}) {
    if (!this.isOfflineMealId(mealId)) {
      return false;
    }

    const clientId = mealId.replace(OFFLINE_MEAL_PREFIX, '');
    const queue = this.getOfflineMealQueue();
    let updated = false;

    const updatedQueue = queue.map((entry) => {
      if (entry.clientId !== clientId) {
        return entry;
      }

      updated = true;
      return {
        ...entry,
        payload: {
          ...entry.payload,
          ...patch,
        },
      };
    });

    if (updated) {
      this.setOfflineMealQueue(updatedQueue);
    }

    return updated;
  }

  removePendingMeal(mealId) {
    if (!this.isOfflineMealId(mealId)) {
      return false;
    }

    const clientId = mealId.replace(OFFLINE_MEAL_PREFIX, '');
    const queue = this.getOfflineMealQueue();
    const updatedQueue = queue.filter((entry) => entry.clientId !== clientId);
    this.setOfflineMealQueue(updatedQueue);
    return updatedQueue.length !== queue.length;
  }

  async syncQueuedMealMutations(journalId) {
    const queue = this.getOfflineMealMutationQueue();
    if (queue.length === 0) {
      return { appliedCount: 0, failedCount: 0, pendingCount: 0 };
    }

    let appliedCount = 0;
    let failedCount = 0;
    const remainingQueue = [];

    for (const entry of queue) {
      if (String(entry.journalId) !== String(journalId) && String(entry.journalId) !== 'all') {
        remainingQueue.push(entry);
        continue;
      }

      try {
        if (entry.type === 'patchMeal') {
          const mealId = entry.payload?.mealId;
          const patch = entry.payload?.patch || {};

          if (!mealId || this.isOfflineMealId(mealId)) {
            appliedCount += 1;
            continue;
          }

          await this.request(`/meals/${mealId}`, {
            method: 'PATCH',
            headers: {
              ...this.getHeaders(),
              'Content-Type': 'application/merge-patch+json'
            },
            body: JSON.stringify(patch),
          });
          appliedCount += 1;
          continue;
        }

        if (entry.type === 'deleteMeal') {
          const mealId = entry.payload?.mealId;
          if (!mealId || this.isOfflineMealId(mealId)) {
            appliedCount += 1;
            continue;
          }

          const response = await fetch(`${this.baseURL}/meals/${mealId}`, {
            method: 'DELETE',
            headers: this.getHeaders(),
          });

          if (!response.ok && response.status !== 404) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || errorData.message || `Erreur HTTP! status: ${response.status}`);
          }

          appliedCount += 1;
          continue;
        }

        if (entry.type === 'reorderMeals') {
          const orders = Array.isArray(entry.payload?.orders) ? entry.payload.orders : [];
          const validOrders = orders.filter(
            (order) => order?.mealId && !this.isOfflineMealId(order.mealId)
          );

          if (validOrders.length === 0) {
            appliedCount += 1;
            continue;
          }

          const updatePromises = validOrders.map((order) =>
            this.request(`/meals/${order.mealId}`, {
              method: 'PATCH',
              headers: {
                ...this.getHeaders(),
                'Content-Type': 'application/merge-patch+json'
              },
              body: JSON.stringify({ sortOrder: order.sortOrder }),
            })
          );

          await Promise.all(updatePromises);
          appliedCount += 1;
          continue;
        }

        appliedCount += 1;
      } catch (error) {
        if (this.isNotFoundError(error)) {
          appliedCount += 1;
          continue;
        }

        failedCount += 1;
        remainingQueue.push(entry);

        if (!this.isNetworkError(error)) {
          console.warn('Synchronisation mutation repas hors ligne échouée:', error);
        }
      }
    }

    this.setOfflineMealMutationQueue(remainingQueue);

    const pendingCount = remainingQueue.filter(
      (entry) => String(entry.journalId) === String(journalId) || String(entry.journalId) === 'all'
    ).length;

    return { appliedCount, failedCount, pendingCount };
  }

  async syncQueuedMeals(journalId) {
    if (!journalId) {
      return { syncedMeals: [], syncedMutations: 0, failedCount: 0, pendingCount: 0 };
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const pendingCount = this.getPendingOfflineMeals(journalId).length
        + this.getOfflineMealMutationQueue().filter((entry) => String(entry.journalId) === String(journalId) || String(entry.journalId) === 'all').length;
      return { syncedMeals: [], syncedMutations: 0, failedCount: 0, pendingCount };
    }

    const queue = this.getOfflineMealQueue();
    const syncedMeals = [];
    let failedCount = 0;
    const remainingQueue = [];

    for (const entry of queue) {
      if (String(entry.journalId) !== String(journalId)) {
        remainingQueue.push(entry);
        continue;
      }

      try {
        const syncedMeal = await this.request('/meals', {
          method: 'POST',
          body: JSON.stringify(entry.payload),
        });
        syncedMeals.push(syncedMeal);
      } catch (error) {
        failedCount += 1;
        remainingQueue.push(entry);

        if (!this.isNetworkError(error)) {
          console.warn('Synchronisation repas hors ligne échouée:', error);
        }
      }
    }

    this.setOfflineMealQueue(remainingQueue);

    const mutationSync = await this.syncQueuedMealMutations(journalId);

    return {
      syncedMeals,
      syncedMutations: mutationSync.appliedCount,
      failedCount: failedCount + mutationSync.failedCount,
      pendingCount: remainingQueue.filter((entry) => String(entry.journalId) === String(journalId)).length + mutationSync.pendingCount,
    };
  }

  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || `Erreur HTTP! status: ${response.status}`);
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return null;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return null;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getHeaders(),
      ...options,
    };

    try {
      const response = await fetch(url, config);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur requête API Journal:', error);
      throw error;
    }
  }

  async getUserJournal() {
    try {
      // GET /journals retourne directement le journal (pas un tableau)
      const journal = await this.request('/journals');

      if (journal && journal.id) {
        return journal;
      }

      throw new Error('Aucun journal trouvé pour cet utilisateur');
    } catch (error) {
      console.error('Erreur lors du chargement du journal:', error);
      throw error;
    }
  }

  async getJournalById(journalId) {
    try {
      return await this.request(`/journals/${journalId}`);
    } catch (error) {
      console.error('Erreur lors du chargement du journal:', error);
      throw error;
    }
  }

  // ===== MÉTHODES POUR LES REPAS =====
  async addMeal(journalId, mealData) {
    try {
      const newMeal = {
        name: mealData.name || "Nouveau repas",
        mealTime: mealData.mealTime || null,
        journal: `/api/journals/${journalId}`
      };

      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return this.queueMealForOffline(journalId, newMeal);
      }

      return await this.request('/meals', {
        method: 'POST',
        body: JSON.stringify(newMeal),
      });
    } catch (error) {
      if (this.isNetworkError(error)) {
        const newMeal = {
          name: mealData.name || "Nouveau repas",
          mealTime: mealData.mealTime || null,
          journal: `/api/journals/${journalId}`
        };
        return this.queueMealForOffline(journalId, newMeal);
      }
      console.error('Erreur lors de l\'ajout du repas:', error);
      throw error;
    }
  }

  async updateMeal(mealId, mealData, journalId = null) {
    if (this.isOfflineMealId(mealId)) {
      return { queued: this.updatePendingMeal(mealId, mealData), pending: true };
    }

    const { foods, ...mealDataWithoutFoods } = mealData;

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.queueMealMutation(journalId, 'patchMeal', {
        mealId,
        patch: mealDataWithoutFoods,
      });
      return { queued: true };
    }

    try {
      const data = await this.request(`/meals/${mealId}`, {
        method: 'PATCH',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/merge-patch+json'
        },
        body: JSON.stringify(mealDataWithoutFoods),
      });
      return { queued: false, data };
    } catch (error) {
      if (this.isNetworkError(error)) {
        this.queueMealMutation(journalId, 'patchMeal', {
          mealId,
          patch: mealDataWithoutFoods,
        });
        return { queued: true };
      }
      console.error('Erreur lors de la mise à jour du repas:', error);
      throw error;
    }
  }

  async deleteMeal(mealId, journalId = null) {
    if (this.isOfflineMealId(mealId)) {
      return { queued: false, pending: true };
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.queueMealMutation(journalId, 'deleteMeal', { mealId });
      return { queued: true };
    }

    try {
      const url = `${this.baseURL}/meals/${mealId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { queued: false };
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `Erreur HTTP! status: ${response.status}`);
      }

      return { queued: false };
    } catch (error) {
      if (this.isNetworkError(error)) {
        this.queueMealMutation(journalId, 'deleteMeal', { mealId });
        return { queued: true };
      }
      console.error('Erreur lors de la suppression du repas:', error);
      throw error;
    }
  }

  // ===== MÉTHODES POUR LES ALIMENTS =====
  async addFoodItem(mealId, foodData) {
    try {
      const openFoodFactsData = foodData.openFoodFactsData?.product
        || foodData.openFoodFactsData?.data?.product
        || foodData.openFoodFactsData
        || null;
      const newFoodItem = {
        barcode: foodData.barcode
          || openFoodFactsData?.code
          || openFoodFactsData?._id
          || openFoodFactsData?.id
          || '',
        name: foodData.name,
        quantity: foodData.quantity || 100,
        macronutrients: this.resolveFoodMacronutrients(foodData),
        ingredients: foodData.ingredients || [],
        sourceData: openFoodFactsData,
        note: foodData.note || '',
        meal: `/api/meals/${mealId}`
      };

      if (Number.isFinite(foodData.sortOrder)) {
        newFoodItem.sortOrder = Number(foodData.sortOrder);
      }

      if (foodData.targetDate) {
        const targetDate = String(foodData.targetDate).trim();
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const hasTime = targetDate.includes('T');
        const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(targetDate);
        const offsetMinutes = now.getTimezoneOffset();
        const offsetSign = offsetMinutes > 0 ? '-' : '+';
        const offsetAbs = Math.abs(offsetMinutes);
        const offsetHours = String(Math.floor(offsetAbs / 60)).padStart(2, '0');
        const offsetMins = String(offsetAbs % 60).padStart(2, '0');
        const tzOffset = `${offsetSign}${offsetHours}:${offsetMins}`;
        const withTime = hasTime
          ? targetDate
          : `${targetDate}T${hours}:${minutes}:${seconds}`;
        newFoodItem.createdAt = hasTimezone ? withTime : `${withTime}${tzOffset}`;
      }

      return await this.request('/food-items', {
        method: 'POST',
        body: JSON.stringify(newFoodItem),
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'aliment:', error);
      throw error;
    }
  }

  async updateFoodItem(foodId, foodData) {
    try {
      return await this.request(`/food-items/${foodId}`, {
        method: 'PATCH',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/merge-patch+json'
        },
        body: JSON.stringify(foodData),
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'aliment:', error);
      throw error;
    }
  }

  async deleteFoodItem(foodId) {
    try {
      await this.request(`/food-items/${foodId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'aliment:', error);
      throw error;
    }
  }

  async loadUserJournal() {
    try {
      const journal = await this.getUserJournal();

      let mealsWithFoods = [];
      if (journal.meals && journal.meals.length > 0) {
        mealsWithFoods = journal.meals.map((meal) => this.normalizeMeal(meal));
      }

      return {
        id: journal.id,
        meals: mealsWithFoods,
        settings: this.parseSettings(journal.settings || []),
        createdAt: journal.createdAt,
        updatedAt: journal.updatedAt
      };
    } catch (error) {
      console.error('Erreur lors du chargement du journal utilisateur:', error);
      throw error;
    }
  }

  parseSettings(settingsArray) {
    const settings = {};
    settingsArray.forEach(setting => {
      if (setting.key) {
        settings[setting.key] = setting.value;
      }
    });
    return settings;
  }

  async getNutritionalGoals(journalId) {
    try {
      const journal = await this.getJournalById(journalId);
      const settings = this.parseSettings(journal.settings || []);

      return {
        targetCalories: parseInt(settings.targetCalories) || 2000,
        targetProtein: parseInt(settings.targetProtein) || 150,
        targetCarbs: parseInt(settings.targetCarbs) || 250,
        targetFats: parseInt(settings.targetFats) || 70,
        waterTarget: parseInt(settings.waterTarget) || 2000,
        waterTracking: settings.waterTracking === 'true',
        displayedMacros: this.mapLegacyMacroKeys(
          this.normalizeMacros(settings.displayedMacros, DEFAULT_FOOD_DETAIL_MACROS)
        )
      };
    } catch (error) {
      console.error('Erreur lors du chargement des objectifs:', error);
      return {
        targetCalories: 2000,
        targetProtein: 150,
        targetCarbs: 250,
        targetFats: 70,
        waterTarget: 2000,
        waterTracking: true,
        displayedMacros: [...DEFAULT_FOOD_DETAIL_MACROS]
      };
    }
  }

  filterMealsByDate(meals, date) {
    const dateKey = date instanceof Date ? date.toISOString().split('T')[0] : date;

    return meals.map(meal => ({
      ...this.normalizeMeal(meal),
      foods: (meal.foods || [])
        .map((food) => this.normalizeFoodItem(food))
        .filter(food => {
          if (!food.createdAt) return false;
          const foodDateKey = food.createdAt.split('T')[0];
          return foodDateKey === dateKey;
        })
        .sort((foodA, foodB) => {
          const sortA = Number.isFinite(Number(foodA?.sortOrder)) ? Number(foodA.sortOrder) : Number.MAX_SAFE_INTEGER;
          const sortB = Number.isFinite(Number(foodB?.sortOrder)) ? Number(foodB.sortOrder) : Number.MAX_SAFE_INTEGER;

          if (sortA !== sortB) {
            return sortA - sortB;
          }

          const createdA = foodA?.createdAt ? Date.parse(foodA.createdAt) : 0;
          const createdB = foodB?.createdAt ? Date.parse(foodB.createdAt) : 0;

          if (createdA !== createdB) {
            return createdA - createdB;
          }

          return (foodA?.id || 0) - (foodB?.id || 0);
        })
    }));
  }

  updateAuthToken(newToken) {
    this.token = newToken;
    setAuthToken(newToken);
  }

  clearAuthToken() {
    this.token = null;
    clearAuthToken();
  }

  async updateMealsOrder(meals, journalId = null) {
    const orders = meals
      .filter((meal) => !this.isOfflineMealId(meal.id))
      .map((meal, index) => ({
        mealId: meal.id,
        sortOrder: Number.isFinite(meal.sortOrder) ? meal.sortOrder : index,
      }));

    if (orders.length === 0) {
      return { success: true, queued: false };
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.queueMealMutation(journalId, 'reorderMeals', { orders });
      return { success: true, queued: true };
    }

    try {
      const updatePromises = orders.map((order) =>
        this.request(`/meals/${order.mealId}`, {
          method: 'PATCH',
          headers: {
            ...this.getHeaders(),
            'Content-Type': 'application/merge-patch+json'
          },
          body: JSON.stringify({ sortOrder: order.sortOrder }),
        })
      );

      await Promise.all(updatePromises);
      return { success: true, queued: false };
    } catch (error) {
      if (this.isNetworkError(error)) {
        this.queueMealMutation(journalId, 'reorderMeals', { orders });
        return { success: true, queued: true };
      }
      console.error('Erreur lors de la mise à jour de l\'ordre des repas:', error);
      throw error;
    }
  }

  async updateFoodItemsOrder(foods) {
    const orders = foods
      .filter((food) => Number.isFinite(Number(food?.id)))
      .map((food, index) => ({
        foodId: food.id,
        sortOrder: Number.isFinite(Number(food.sortOrder)) ? Number(food.sortOrder) : index,
      }));

    if (orders.length === 0) {
      return { success: true };
    }

    try {
      await Promise.all(
        orders.map((order) =>
          this.request(`/food-items/${order.foodId}`, {
            method: 'PATCH',
            headers: {
              ...this.getHeaders(),
              'Content-Type': 'application/merge-patch+json'
            },
            body: JSON.stringify({ sortOrder: order.sortOrder }),
          })
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'ordre des aliments:', error);
      throw error;
    }
  }
}

const journalService = new JournalService();

export default journalService;
